/**
 * Privacy/consent state — the Plausible-only model (adapted from Gamerhood
 * S5, minus all GTM/Consent-Mode plumbing).
 *
 * There is deliberately NO blocking consent modal: the only cookies are
 * strictly-necessary auth cookies (Phase 2 refresh token) and analytics is
 * self-hosted cookieless Plausible. The dismissible privacy notice bar is
 * the universal surface; this module tracks its dismissal, the analytics
 * opt-out, and honors GPC. Posture + copy flagged for legal review.
 *
 * Analytics opt-out mechanics: Plausible's official localStorage flag
 * (`plausible_ignore=true`), which its tracker checks on every event. The
 * index.html PLAUSIBLE bootstrap (IMT house template) ALSO load-gates on this
 * flag — under opt-out / GPC the cookieless tracker script is never injected
 * at all. This module keeps that flag authoritative and, for mid-session
 * changes, drives the bootstrap's window.__loadPlausible / __unloadPlausible
 * so the resident script matches the current choice without a page reload
 * (plausible_ignore already suppresses any in-flight tracker per event, so no
 * forced reload is needed on revoke).
 *
 * Consent changes are queued locally and flushed to POST /privacy/consent
 * once the API ships (Phase 2) so the record survives the platform landing
 * after the site.
 */

export const CONSENT_VERSION = '2026-07-07';

const PREFS_KEY = 'la:consent';
const PLAUSIBLE_IGNORE_KEY = 'plausible_ignore';
const QUEUE_KEY = 'la:consentQueue';

export type ConsentPrefs = {
  version: string;
  analytics: boolean;
  /** ISO timestamp of notice-bar dismissal; null = bar still shows. */
  noticeDismissedAt: string | null;
  /** True when the opt-out came from a GPC signal rather than user action. */
  gpcApplied: boolean;
};

type ConsentEvent = {
  type: 'ANALYTICS_OPT_OUT' | 'ANALYTICS_OPT_IN' | 'NOTICE_DISMISSED' | 'GPC_APPLIED';
  version: string;
  at: string;
};

const DEFAULT_PREFS: ConsentPrefs = {
  version: CONSENT_VERSION,
  analytics: true,
  noticeDismissedAt: null,
  gpcApplied: false,
};

export function readPrefs(): ConsentPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    const parsed = JSON.parse(raw) as ConsentPrefs;
    // Version bump re-surfaces the notice bar but keeps the opt-out choice.
    if (parsed.version !== CONSENT_VERSION) {
      return { ...DEFAULT_PREFS, analytics: parsed.analytics };
    }
    return parsed;
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

function writePrefs(prefs: ConsentPrefs): void {
  localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
}

function queueEvent(type: ConsentEvent['type']): void {
  // Flushed to POST /privacy/consent when the API lands (Phase 2).
  try {
    const queue = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]') as ConsentEvent[];
    queue.push({ type, version: CONSENT_VERSION, at: new Date().toISOString() });
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(-50)));
  } catch {
    // Storage full/blocked — consent still applies locally.
  }
}

function applyAnalyticsFlag(enabled: boolean): void {
  if (enabled) {
    localStorage.removeItem(PLAUSIBLE_IGNORE_KEY);
  } else {
    localStorage.setItem(PLAUSIBLE_IGNORE_KEY, 'true');
  }
}

/**
 * Reconcile the resident Plausible tracker with the effective analytics choice
 * by driving the index.html bootstrap's load/unload globals. Call AFTER
 * applyAnalyticsFlag so both the loader gate and plausible_ignore agree. Globals
 * are absent in dev/pre-analytics builds (the block is stripped) — optional-chained.
 * `effectivelyOn` must already fold in GPC (which forces analytics off).
 */
function syncPlausibleScript(effectivelyOn: boolean): void {
  if (effectivelyOn) window.__loadPlausible?.();
  else window.__unloadPlausible?.();
}

export function setAnalyticsEnabled(enabled: boolean): ConsentPrefs {
  const prefs = { ...readPrefs(), analytics: enabled, gpcApplied: false };
  writePrefs(prefs);
  applyAnalyticsFlag(enabled);
  // Mid-session: inject or remove the tracker so it matches the new choice.
  // GPC still wins (the Privacy Settings toggle is locked under GPC, but guard
  // anyway) — never load while GPC is asserting opt-out.
  syncPlausibleScript(enabled && !gpcActive());
  queueEvent(enabled ? 'ANALYTICS_OPT_IN' : 'ANALYTICS_OPT_OUT');
  return prefs;
}

export function dismissNotice(): ConsentPrefs {
  const prefs = { ...readPrefs(), noticeDismissedAt: new Date().toISOString() };
  writePrefs(prefs);
  queueEvent('NOTICE_DISMISSED');
  return prefs;
}

export function gpcActive(): boolean {
  return (navigator as { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
}

/**
 * Boot-time wiring (before React mounts): honor GPC by forcing the
 * analytics opt-out, and re-assert the Plausible flag from stored prefs.
 */
export function initConsent(): void {
  const prefs = readPrefs();
  if (gpcActive() && prefs.analytics) {
    const updated: ConsentPrefs = { ...prefs, analytics: false, gpcApplied: true };
    writePrefs(updated);
    applyAnalyticsFlag(false);
    // The bootstrap already self-gated on GPC at boot; reconcile in case a
    // tracker somehow resolved before this ran.
    syncPlausibleScript(false);
    queueEvent('GPC_APPLIED');
    return;
  }
  applyAnalyticsFlag(prefs.analytics);
  syncPlausibleScript(prefs.analytics);
}
